import { API } from 'aws-amplify';
import { isEmpty, cloneDeep } from 'lodash';
import { batch } from 'react-redux';
import axios from 'axios';
import i18n from 'i18next';

import {
  ADD_EDUCATION,
  ADD_LOCATION,
  ADD_SCORE,
  ADD_TERMS,
  EXISTING_USER_ID,
  EXISTING_COGNITO_USERNAME,
  GET_ALL_USERS,
  GET_HIGH_SCHOOLS,
  GET_TOP_MATCHES,
  GET_USER_DATA,
  REGISTER_USER,
  SAVE_REGISTRATION_SOURCE,
  SAVE_RESULTS,
  SAVE_USER_CHOICES,
  SET_ACTION_PLAN,
  SET_AUTH_STATUS,
  SET_CAREER_DEGREE_MODALITY,
  SET_CAREER_BRIGHT_OUTLOOK_MODALITY,
  SET_CAREER_TAB,
  SET_COOKIES_CONSENT,
  SET_DEGREE_CAREER_ID_MAP,
  SET_DEGREE_CHOICES,
  SET_ECAP,
  SET_ECAP_CLASSES_MATCHED,
  SET_ECAP_SUBJECT_DIFFICULTY,
  SET_FAVORITES,
  SET_IMAGE_PAIRS,
  SET_LOADING,
  SET_MODAL_IMAGE,
  SET_ROGUE_USER,
  SET_TENANT_CAREERS,
  SET_TENANT_CONFIG,
  SET_USER,
  SHOW_PROGRESS_BAR,
  USER_LOGOUT_ADMIN,
  USER_LOGOUT_AUTH,
  USER_LOGOUT_ECAP,
  SET_ROLE_PERMISSIONS,
  USER_LOGOUT_PREREGISTRATION,
  UPDATE_USER_PHOTO_URL,
  USER_LOGOUT_ONBOARDING,
  SET_PRE_REGISTRATION_FLAG,
  SET_REGISTER_OPTIONS,
  GET_SKILLS,
  GET_SKILLS_COURSES,
  REGISTRATION_COMPLETE,
  SET_USERS_TAB_FILTERS,
  SET_ROGUE_TENANT_SSO,
  GET_ADVISOR_USER_PROFILE,
  SET_USER_NAME_SEARCH_STRING,
  SET_REGISTRATION_IN_PROGRESS,
  RESET_PAGES,
  SET_CURRENT_LOCALE,
} from './actionTypes';
import { showOnlineDegreesOnly, showBrightCareersOnly } from './selectors';
import { getLocalizedPropValue } from '../util/localization';

const getAmplifyConfig = (body, authToken = null) => {
  return {
    body,
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
    response: true,
  };
};

export const userLogout = () => {
  return async (dispatch) => {
    try {
      batch(() => {
        dispatch({
          type: USER_LOGOUT_AUTH,
          payload: {},
        });
        dispatch({
          type: USER_LOGOUT_ONBOARDING,
          payload: {},
        });
        dispatch({
          type: USER_LOGOUT_PREREGISTRATION,
          payload: {},
        });
        dispatch({
          type: USER_LOGOUT_ECAP,
          payload: {},
        });
        dispatch({
          type: USER_LOGOUT_ADMIN,
          payload: {},
        });
      });
    } catch (error) {
      console.error('Error: ', error.message);
    }
  };
};

export const getData = (userId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ userId }, authToken);

      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/data`,
        init
      );

      const degreeChoices = {
        1: '',
        2: '',
        3: '',
      };

      const careerChoiceObj = {
        1: response.data.data.careerGameResults[0],
        2: response.data.data.careerGameResults[1],
        3: response.data.data.careerGameResults[2],
      };

      /* eslint-disable-next-line */
      for (const x of response.data.data.careerGameResults) {
        if (x.degreeId !== null && x.chosenOrder !== null) {
          /* eslint-disable-next-line */
          const degreeResponse = await API.get(
            'api',
            `/tenant/${getState().tenant.id}/degree/${x.degreeId}`,
            init
          );

          const degreeMatch = degreeResponse.data.data;

          // The chosenOrder can also tell you which tab it was selected for
          if (degreeMatch === undefined) {
            degreeChoices[x.chosenOrder] = '';
          } else {
            degreeChoices[x.chosenOrder] = degreeMatch;
          }

          // Add in the degree match to the degreeCareerIdMap if it isn't there
          // (but a match exists) so users don't get stuck in the path pairing page
          if (!response.data.data.degreeCareerIdMap[x.id]) {
            response.data.data.degreeCareerIdMap[x.id] = [degreeMatch];
          }
        }
      }

      // adding career image to degree
      /* eslint-disable-next-line */
      for (const id in response.data.data.degreeCareerIdMap) {
        const careerMatchingDegree = response.data.data.careerGameResults.find(
          (career) => career.id === Number(id)
        );

        for (
          let i = 0;
          i < response.data.data.degreeCareerIdMap[id].length;
          i++
        ) {
          response.data.data.degreeCareerIdMap[id][i].career_image =
            careerMatchingDegree.image;
        }
      }

      batch(() => {
        dispatch({
          type: SET_ROGUE_USER,
          payload: response.data.data.userIsRogue,
        });
        dispatch({
          type: GET_USER_DATA,
          payload: {
            userData: {
              user: response.data.data.user || {},
              careerGameResults: response.data.data.careerGameResults,
              degreeCareerIdMap: response.data.data.degreeCareerIdMap,
              userIsRogue: response.data.data.userIsRogue,
              outOfDateData: response.data.data.outOfDateData,
            },
          },
        });

        dispatch({
          type: SAVE_USER_CHOICES,
          payload: response.data.data.careerGameResults,
        });

        dispatch({
          type: SET_DEGREE_CHOICES,
          payload: degreeChoices,
        });

        dispatch({
          type: SET_DEGREE_CAREER_ID_MAP,
          payload: {
            careerChoices: careerChoiceObj,
          },
        });

        dispatch({
          type: UPDATE_USER_PHOTO_URL,
          payload: {
            profilePhotoURL: response.data.data.user.profilePhotoUrl,
          },
        });
      });
      return true;
    } catch (error) {
      console.error('User data not retrieved: ', error);
    }
  };
};

export const noUserReady = () => {
  return async (dispatch) => {
    try {
      const tempDegreeChoices = {
        1: '',
        2: '',
        3: '',
      };
      dispatch({
        type: SET_DEGREE_CHOICES,
        payload: tempDegreeChoices,
      });
      // Is it necessary that an action update the redux store?
    } catch (error) {
      console.error('UnAuth user not ready error: ', error);
    }
  };
};

// TODO: Maybe put the API.post() request into a seperate function?
export const addCareerPathChoice = (tenantId, careerPathChoice, authToken) => {
  return async () => {
    try {
      const init = getAmplifyConfig(
        {
          ...careerPathChoice,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/game/savePair`,
        init
      );

      return response;
      // Is it necessary that an action update the redux store?
    } catch (error) {
      console.error('Path-pairing not saved: ', error);
    }
  };
};

export const updateUserProfilePhoto = (userId, authToken, photoURL) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          userProfilePhotoURL: photoURL,
        },
        authToken
      );

      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/saveUserProfilePhoto/${userId}`,
        init
      );
      dispatch({
        type: UPDATE_USER_PHOTO_URL,
        payload: {
          profilePhotoURL: photoURL,
        },
      });
      return response;
    } catch (error) {
      console.error('User profile photo url not saved: ', error);
    }
  };
};

export const userRetakeGameReset = (userId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ userId }, authToken);

      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/data`,
        init
      );

      const degreeChoices = {
        1: '',
        2: '',
        3: '',
      };

      const careerChoices = {
        1: '',
        2: '',
        3: '',
      };

      const userDataUser = response.data.data.user;
      if (userDataUser) userDataUser.hasBeenToDashboard = false;

      batch(() => {
        dispatch({
          type: SET_ROGUE_USER,
          payload: response.data.data.userIsRogue,
        });
        dispatch({
          type: GET_USER_DATA,
          payload: {
            userData: {
              user: userDataUser || {},
              careerGameResults: [],
              degreeCareerIdMap: {},
              userIsRogue: response.data.data.userIsRogue,
            },
          },
        });
        dispatch({
          type: SAVE_USER_CHOICES,
          payload: [],
        });
        dispatch({
          type: SET_DEGREE_CHOICES,
          payload: degreeChoices,
        });
        dispatch({
          type: SET_DEGREE_CAREER_ID_MAP,
          payload: {
            careerChoices,
          },
        });
      });
      return true;
    } catch (error) {
      console.error('User data for user not reset when retaking game: ', error);
    }
  };
};

export const removeProfilePhoto = (authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, authToken);
      // Get the current user with a new route
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user/removePhoto`,
        init
      );
      dispatch({
        type: GET_USER_DATA,
        payload: { userData: response.data.data },
      });
    } catch (error) {
      console.error('Profile photo not removed: ', error);
    }
  };
};

export const removeCareerPathChoice = (
  tenantId,
  careerPathChoice,
  authToken
) => {
  return async () => {
    try {
      const init = getAmplifyConfig(
        {
          ...careerPathChoice,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/game/removePair`,
        init
      );

      return response;
    } catch (error) {
      console.error('Path-pairing not removed: ', error);
    }
  };
};

export const showProgressBar = (careerCardsExpanded) => ({
  type: SHOW_PROGRESS_BAR,
  payload: {
    careerCardsExpanded,
  },
});

export const setCareerTab = (
  careerInfo,
  careerBool,
  currentTab,
  pathsChosenOverall
) => ({
  type: SET_CAREER_TAB,
  payload: {
    careerInfo,
    careerTabSelected: careerBool,
    currentTab,
    pathsChosenOverall,
  },
});

export const setLoading = (bool) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_LOADING, payload: bool });
    } catch (error) {
      console.error('Could not set loading ', error);
    }
  };
};

export const addEducation = (
  educationLevel,
  sliderBarValue,
  educationIcon,
  graduationYear
) => ({
  type: ADD_EDUCATION,
  payload: {
    educationLevel,
    sliderBarValue,
    educationIcon,
    graduationYear,
  },
});

export const addLocation = (location) => ({
  type: ADD_LOCATION,
  payload: {
    country: location.country,
    hasTerritories: location.hasTerritories,
    territory: location.territory,
  },
});

export const addTerms = (terms) => ({
  type: ADD_TERMS,
  payload: {
    agreedToTerms: terms.agreedToTerms,
    termsVersionNumber: terms.termsVersionNumber,
  },
});

export const addScore = (results) => ({
  type: ADD_SCORE,
  payload: {
    results,
  },
});

export const setUser = (user) => ({
  type: SET_USER,
  payload: {
    user,
  },
});

export const setRegistrationInProgress = (bool) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_REGISTRATION_IN_PROGRESS,
        payload: bool,
      });
    } catch (error) {
      console.error('Could not set registration in progress ', error);
    }
  };
};

// TODO: Maybe call other action to clear onboarding if auth status is null/false
export const setAuthStatus = (status) => ({
  type: SET_AUTH_STATUS,
  payload: {
    authenticated: status.authenticated,
    session: status.session,
  },
});

export const setRogueTenantSSO = (isRogueTenantSSO) => ({
  type: SET_ROGUE_TENANT_SSO,
  payload: {
    isRogueTenantSSO,
  },
});

export const setTenantCareers = (allTenantCareers) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_TENANT_CAREERS,
        payload: { allTenantCareers },
      });
    } catch (error) {
      console.error('Careers could not be set for tenant. ', error);
    }
  };
};

export const getCurrentUserId = (authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, authToken);
      // Get the current user with a new route
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user`,
        init
      );

      await dispatch({
        type: EXISTING_USER_ID,
        payload: response.data.data.userId,
      });
      await dispatch({
        type: SET_ROGUE_USER,
        payload: response.data.data.userIsRogue,
      });

      return response.data.data.userId;
    } catch (error) {
      console.error('User id not retrieved: ', error);
    }
  };
};

export const getTopMatches = (score) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({
        riasecValues: score,
      });
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/game/getTopMatches`,
        init
      );

      dispatch({
        type: GET_TOP_MATCHES,
        payload: {
          topThreeMatches: response.data.data.careerGameResults,
        },
      });
    } catch (error) {
      console.error('Top matches not found: ', error);
    }
  };
};

export const getSkills = () => {
  return async (dispatch, getState) => {
    try {
      const init = {
        headers: {
          Authorization: getState().auth.jwtToken,
        },
      };
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/game/getSkills`,
        init
      );
      const skillsObj = {};
      response.data.skills.forEach((skill) => {
        skillsObj[`${skill.skillEn}`] = { ...skill };
      });
      dispatch({
        type: GET_SKILLS,
        payload: {
          skills: skillsObj,
        },
      });
    } catch (error) {
      console.error('Skills not found: ', error);
    }
  };
};

export const getSkillsCourses = () => {
  return async (dispatch, getState) => {
    try {
      const init = {
        headers: {
          Authorization: getState().auth.jwtToken,
        },
      };
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/getSkillsCourses`,
        init
      );
      dispatch({
        type: GET_SKILLS_COURSES,
        payload: {
          skillsCourses: response.data,
        },
      });
    } catch (error) {
      console.error('Skills courses not found: ', error);
    }
  };
};

export const getAssignableStates = () => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/getAssignableStates`,
        init
      );
      return response.data.data;
    } catch (error) {
      console.error('Failed to get assignable states: ', error);
    }
  };
};

export const uploadTenantPhotoToS3 = (file, photoType) => {
  return async (dispatch, getState) => {
    try {
      const init = {
        body: {
          title: photoType,
        },
        headers: {
          Authorization: getState().auth.jwtToken,
        },
        response: true,
      };
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/handleTenantPhotos`,
        init
      );
      const options = { headers: { 'Content-Type': 'image/jpeg' } };
      const putResponse = await axios.put(
        response.data.data.uploadURL,
        file,
        options
      );
      const s3BucketAddress = putResponse.config.url.substr(
        0,
        putResponse.config.url.indexOf('?')
      );
      return s3BucketAddress;
    } catch (error) {
      console.error('Failed to get assignable states: ', error);
    }
  };
};

export const getSchools = (territory) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({});
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/highSchools/${territory}`,
        init
      );
      const schoolArray = [];
      response.data.data.schools.forEach((school) => {
        schoolArray.push({
          id: school.id,
          value: school.title_en,
          label: school.title_en,
          checked: false,
        });
      });
      schoolArray.push({ value: 'Other', label: 'Other' });
      dispatch({
        type: GET_HIGH_SCHOOLS,
        payload: {
          highSchools: schoolArray,
        },
      });
    } catch (error) {
      console.error('High Schools not found: ', error);
    }
  };
};

export const getAssignableSchools = (territory, tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getAssignableSchools/${territory}`,
        init
      );
      const schoolArray = [];
      response.data.data.schools.forEach((school) => {
        schoolArray.push({
          id: school.id,
          value: school.title_en,
          label: school.title_en,
          checked: false,
        });
      });
      return schoolArray;
    } catch (error) {
      console.error('Assignable High Schools not found: ', error);
    }
  };
};

export const getDistricts = (territory, tenantId) => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/hsDistricts/${territory}`,
        config
      );
      const districts = [];
      response.data.data.districts.forEach((district) => {
        districts.push({
          id: district.id,
          value: district.title_en,
          label: district.title_en,
          checked: false,
        });
      });
      return districts;
    } catch (error) {
      console.error('High School Districts not found: ', error);
    }
  };
};

export const registerUser = (newUser, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          dateOfBirth: newUser.dateOfBirth,
          country: newUser.country,
          state: newUser.state || null,
          highSchool: newUser.highSchool || null,
          graduationYear: newUser.graduationYear || null,
          regSource: newUser.registrationSource || null,
          receiveCommunications: newUser.receiveCommunications,
          educationLevel: newUser.educationLevel,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/registration`,
        init
      );
      await dispatch({
        type: REGISTER_USER,
        payload: { userId: response.data.data.user.id },
      });

      dispatch(setLoading(false));

      return response.data.data.user.id;
    } catch (error) {
      console.error('User not found: ', error);
    }
  };
};

export const clearRegistrationData = () => ({
  type: REGISTRATION_COMPLETE,
});

export const setUserPushNotifications = (pushNotifications, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          ...pushNotifications,
        },
        authToken
      );

      await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/pushNotifications`,
        init
      );
      dispatch(setLoading(false));
    } catch (error) {
      console.error('Could not set push notifications: ', error);
    }
  };
};

export const savePair = (user, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          riasecValues: user.riasecValues,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/game/savePair`,
        init
      );

      dispatch({
        type: SAVE_RESULTS,
        payload: {
          userData: {
            careerGameResults: response.data.data.careerGameResults,
            degreeCareerIdMap: response.data.data.degreeCareerIdMap,
          },
        },
      });
    } catch (error) {
      console.error('Path pairing not saved: ', error);
    }
  };
};

export const saveResults = (user, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          riasecValues: user.riasecValues,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/game/saveResults`,
        init
      );

      dispatch({
        type: SAVE_RESULTS,
        payload: {
          userData: {
            careerGameResults: response.data.data.careerGameResults,
            degreeCareerIdMap: response.data.data.degreeCareerIdMap,
          },
        },
      });

      return response.data.data;
    } catch (error) {
      console.error('Results not saved: ', error);
    }
  };
};

export const setPreRegistrationFlag = (value) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_PRE_REGISTRATION_FLAG,
        payload: { value },
      });
    } catch (error) {
      console.error('Pre-registration flag not set: ', error);
    }
  };
};

export const setRegisterOptions = (value) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_REGISTER_OPTIONS,
        payload: { value },
      });
    } catch (error) {
      console.error('Register options not set: ', error);
    }
  };
};

export const updateUsersTabFilters = (filters) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_USERS_TAB_FILTERS,
        payload: { filters },
      });
    } catch (error) {
      console.error('Users tab filters not set: ', error);
    }
  };
};

export const setUserNameSearchString = (string) => {
  return async (dispatch) => {
    try {
      await dispatch({
        type: SET_USER_NAME_SEARCH_STRING,
        payload: { string },
      });
    } catch (error) {
      console.error('User name search string not set: ', error);
    }
  };
};

export const saveNewCareer = (career, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          careers: [career],
        },
        authToken
      );

      await API.post(
        'api',
        `/tenant/${getState().tenant.id}/game/saveNewCareer`,
        init
      );
    } catch (error) {
      console.error('Career not saved: ', error);
    }
  };
};

export const getTenantConfig = (tenantName) => {
  return async (dispatch) => {
    try {
      const init = getAmplifyConfig();
      const response = await API.get(
        'api',
        `/tenant/${tenantName}/config`,
        init
      );
      dispatch({
        type: SET_TENANT_CONFIG,
        payload: {
          id: response.data.data.tenant.id,
          primary: response.data.data.tenant.primaryColor,
          secondary: response.data.data.tenant.secondaryColor,
          hero: response.data.data.tenant.hero,
          heroImgPortrait: response.data.data.tenant.heroImgPortrait,
          logo: response.data.data.tenant.logo,
          name: response.data.data.tenant.name,
          terms_info: JSON.parse(response.data.data.tenant.termsInfo),
          features: response.data.data.tenant.features,
          logoLink: response.data.data.tenant.logoLink,
          dashboardHero: response.data.data.tenant.dashboardHero,
          additionalLogoLink: response.data.data.tenant.additionalLogoLink,
          buttonInfo: {
            button1: response.data.data.tenant.button1,
            button2: response.data.data.tenant.button2,
            button3: response.data.data.tenant.button3,
            button4: response.data.data.tenant.button4,
            button5: response.data.data.tenant.button5,
            button6: response.data.data.tenant.button6,
          },
          hasSSO: response.data.data.tenant.hasSSO,
          published: response.data.data.tenant.published,
        },
      });
    } catch (error) {
      const init = getAmplifyConfig();
      const response = await API.get('api', '/tenant/ASU/config', init);

      dispatch({
        type: SET_TENANT_CONFIG,
        payload: {
          id: response.data.data.tenant.id,
          primary: response.data.data.tenant.primaryColor,
          secondary: response.data.data.tenant.secondaryColor,
          hero: response.data.data.tenant.hero,
          logo: response.data.data.tenant.logo,
          name: response.data.data.tenant.name,
          terms_info: JSON.parse(response.data.data.tenant.termsInfo),
          features: response.data.data.tenant.features,
          logoLink: response.data.data.tenant.logoLink,
          dashboardHero: response.data.data.tenant.dashboardHero,
          additionalLogoLink: response.data.data.tenant.additionalLogoLink,
          hasSSO: response.data.data.tenant.hasSSO,
        },
      });
    }
  };
};

export const getAllUsersForAdvisor = (
  tenantId,
  currentPage = 0,
  limit = false
) => {
  return async (dispatch, getState) => {
    try {
      const usersFilters = cloneDeep(getState().admin.permissions.usersFilters);
      const checkedFilters = {};
      Object.keys(usersFilters).map((key) => {
        if (!usersFilters[key]) return;
        const tempArr = usersFilters[key].filter((x) => x.checked === true);
        if (tempArr.length > 0) {
          checkedFilters[key] = tempArr;
        }
        return checkedFilters;
      });
      const searchString = getState().admin.userNameSearchString;
      const config = getAmplifyConfig(
        { checkedFilters, searchString, currentPage, limit },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/users`,
        config
      );
      if (response.data === null) {
        dispatch({ type: GET_ALL_USERS, payload: null });
      } else {
        dispatch({ type: GET_ALL_USERS, payload: response.data.data });
      }
    } catch (error) {
      console.error('Users could not be retreived. ', error);
    }
  };
};

export const resetUsersTabPages = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: RESET_PAGES, payload: 1 });
    } catch (error) {
      console.error('Unable to reset pages');
    }
  };
};

export const getUserCounts = () => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/usersCount`,
        config
      );
      return response.data.data;
    } catch (error) {
      console.error('User counts could not be retreived', error);
    }
  };
};

export const insertTenantFeatures = (tenantId, ids) => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig({ ids }, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertTenantFeatures`,
        config
      );
      return response.data.data;
    } catch (error) {
      console.error('Tenant features could not be inserted', error);
    }
  };
};

export const getUserCountsCsv = () => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/getUsersCountCsv`,
        config
      );
      return response.data.data;
    } catch (error) {
      console.error('User counts csv data could not be retreived', error);
    }
  };
};

export const saveNewTenantForUser = (userId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const tenantId = getState().onboarding.userData.user.tenantId;
      const config = getAmplifyConfig(
        { newTenantId: getState().tenant.id },
        authToken
      );

      await API.post(
        'api',
        `/tenant/${tenantId}/user/${userId}/newTenant`,
        config
      );
      await dispatch({
        type: SET_ROGUE_USER,
        payload: false,
      });
    } catch (error) {
      console.error('New tenant could not be saved for user', error);
    }
  };
};

export const setImagePairs = (imagePairs = []) => {
  return async (dispatch) => {
    dispatch({
      type: SET_IMAGE_PAIRS,
      payload: { imagePairs },
    });
  };
};

export const clearImagePairs = () => setImagePairs([]);

export const getImagePairs = (tenantId) => {
  return async (dispatch, getState) => {
    const imagePairsCopy = [...(getState().preregistration.imagePairs || [])];

    try {
      await dispatch(clearImagePairs());

      const config = getAmplifyConfig({ tenantId });
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/game/getImagePairs`,
        config
      );

      dispatch(setImagePairs(response.data.data.imagePairs));
    } catch (error) {
      console.error('Could not get image pairs', error);

      // revert on error
      dispatch(setImagePairs(imagePairsCopy));
    }
  };
};

export const checkUser = (email) => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig({ email });
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user-email/${email}`,
        config
      );
      if (typeof response.data.data.user_id === 'number') {
        dispatch({
          type: EXISTING_USER_ID,
          payload: response.data.data.user_id,
        });
      }
      dispatch({
        type: EXISTING_COGNITO_USERNAME,
        payload: response.data.data.cognito_username,
      });

      return response.data.data.user_id;
    } catch (error) {
      console.error('Could not check for existence of user', error);
    }
  };
};

export const saveRegistrationSource = (registrationSource) => {
  return async (dispatch) => {
    dispatch({
      type: SAVE_REGISTRATION_SOURCE,
      payload: { registrationSource },
    });
  };
};

export const getUserData = (userId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ userId }, authToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/data`,
        init
      );

      await dispatch({
        type: SET_ROGUE_USER,
        payload: response.data.data.userIsRogue,
      });
      dispatch({
        type: GET_USER_DATA,
        payload: { userData: response.data.data },
      });
    } catch (error) {
      console.error('Could not get user data ', error);
    }
  };
};

export const saveUserChoices = (careers, authToken) => {
  return async (dispatch, getState) => {
    try {
      const config = getAmplifyConfig(
        { userChosenChoices: careers },
        authToken
      );

      await API.post(
        'api',
        `/tenant/${getState().tenant.id}/game/setChosen`,
        config
      );

      const filtered = careers.filter((career) => career !== null);

      dispatch({
        type: SAVE_USER_CHOICES,
        payload: filtered,
      });
      dispatch(getData(`${getState().onboarding.userData.user.id}`, authToken));
    } catch (error) {
      console.error('Could not save user career choices: ', error);
    }
  };
};

export const getTenantCareers = (tenantId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(authToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getTenantCareers`,
        init
      );

      const result = response.data.data.careerArray.map((career) => ({
        ...career,
        // for now, 'titleEn' will actually contain the translated title in the current language
        titleEn: getLocalizedPropValue(
          career,
          'title',
          getState().currentLocale
        ),
      }));

      return result;
    } catch (error) {
      console.error('Failed to get careers by tenant id: ', error);
    }
  };
};

export const getTenantDegrees = (tenantId, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(authToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getTenantDegrees`,
        init
      );

      const result = response.data.data.map((degree) => ({
        ...degree,
        // for now, several translated values of degree properties in the current language will be assigned to the 'En' property
        titleEn: getLocalizedPropValue(
          degree,
          'title',
          getState().currentLocale
        ),
        descriptionEn: getLocalizedPropValue(
          degree,
          'description',
          getState().currentLocale
        ),
        academicProgramType: getLocalizedPropValue(
          degree,
          'academicProgramType',
          getState().currentLocale
        ),
        degreeTypeEn: getLocalizedPropValue(
          degree,
          'degreeType',
          getState().currentLocale
        ),
        firstMathReq: getLocalizedPropValue(
          degree,
          'firstMathReq',
          getState().currentLocale
        ),
      }));

      return result;
    } catch (error) {
      console.error('Failed to get degrees by tenant id: ', error);
    }
  };
};

export const getDegree = (tenantId, degreeId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/degree/${degreeId}`,
        init
      );
      const result = response.data.data;

      return result;
    } catch (error) {
      console.error('Could not get degree by id: ', error);
    }
  };
};

export const getAllTenantHSClasses = (tenantId, authToken, acadPlanArray) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ acadPlanArray }, authToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getHSClassesByTenant`,
        init
      );

      // use the title and description for the current language
      const result = response.data.data.map((classItem) => ({
        ...classItem,
        requirement_title: getLocalizedPropValue(
          classItem,
          'title',
          getState().currentLocale
        ),
        learning_outcomes: getLocalizedPropValue(
          classItem,
          'learningOutcomes',
          getState().currentLocale
        ),
      }));

      return result;
    } catch (error) {
      console.error('Failed to get all hs_classes for tenant: ', error);
    }
  };
};

export const getTenantHSClassesForAcadplans = (tenantId, acadPlans) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({
        acadPlans,
      });
      // Use POST so that we can parse the body out on the b/e and use the acadPlan arrays
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getTenantHSClassesForAcadplans`,
        init
      );

      // use the title and description for the current language
      const result = response.data.data.map((classItem) => ({
        ...classItem,
        // for now, 'titleEn' will actually contain the translated title in the current language
        titleEn: getLocalizedPropValue(
          classItem,
          'title',
          getState().currentLocale
        ),
      }));

      return result;
    } catch (error) {
      console.error('Failed to get hs_classes by tenant id: ', error);
    }
  };
};

export const getDegreeSubjectDifficultyMapping = (tenantId, acadPlan) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { acad_plan: acadPlan },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getDegreeSubjectDifficultyMapping`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not get degree subject difficulty mapping: ', error);
    }
  };
};

export const updateDegreeSubjectDifficultyMapping = (
  tenantId,
  acadPlan,
  degreeSubjectDifficultyMapping
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { acad_plan: acadPlan, ...degreeSubjectDifficultyMapping },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/updateDegreeSubjectDifficultyMapping`,
        init
      );

      return response;
    } catch (error) {
      console.error(
        'Could not update degree subject difficulty mapping: ',
        error
      );
    }
  };
};

export const updateHasBeenToDashboard = (authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, authToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/updateHasBeenToDashboard`,
        init
      );

      dispatch({
        type: GET_USER_DATA,
        payload: { userData: response.data.data },
      });
    } catch (error) {
      console.error('User has been to dashboard not updated: ', error);
    }
  };
};

export const showCareerPathOnlineDegreesOnly = (onlineOnly) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: SET_CAREER_DEGREE_MODALITY,
        payload: onlineOnly,
      });
    } catch (error) {
      console.error('Could not set onlineOnly: ', error);
    }
  };
};

export const toggleCareerPathOnlineDegreesOnly = () => {
  return async (dispatch, getState) => {
    const currentVal = showOnlineDegreesOnly(getState());

    try {
      dispatch({
        type: SET_CAREER_DEGREE_MODALITY,
        payload: !currentVal,
      });
    } catch (error) {
      console.error('Could not toggle onlineOnly: ', error);
    }
  };
};

export const toggleBrightOutlookCareerSearch = () => {
  return async (dispatch, getState) => {
    const currentVal = showBrightCareersOnly(getState());

    try {
      dispatch({
        type: SET_CAREER_BRIGHT_OUTLOOK_MODALITY,
        payload: !currentVal,
      });
    } catch (error) {
      console.error('Could not toggle bright outlook only: ', error);
    }
  };
};

export const getHSClassesByAcadplanV2 = (tenantId, acadPlan, updateStore) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ acadPlan }, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getHSClassesByAcadplanV2`,
        init
      );

      const academicId = (hsClasses) => {
        switch (hsClasses.subject) {
          case 'English':
            return 1;
          case 'Math':
            return 2;
          case 'Science':
            return 3;
          case 'Social Studies':
            return 4;
          case 'Fine Art/CTE':
            return 5;
          case 'Foreign Language':
            return 6;
          default:
            return 7;
        }
      };

      // add academic_area_id to courses and use translations for course titles
      const result = response.data.data?.map((course) => ({
        ...course,
        academic_area_id: academicId(course),
        // for now, 'titleEn' will actually contain the translated title in the current language
        titleEn: getLocalizedPropValue(
          course,
          'title',
          getState().currentLocale
        ),
      }));

      if (updateStore) {
        dispatch({
          type: SET_ECAP_SUBJECT_DIFFICULTY,
          payload: { subjectDifficulty: result },
        });
      }

      return result;
    } catch (error) {
      console.error('Could not retrieve HS classes: ', error);
    }
  };
};

export const getHSClassesBySubjectDifficultyMapping = (
  tenantId,
  subjectDifficultyMapping
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { ...subjectDifficultyMapping },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getHSClassesBySubjectDifficultyMapping`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve HS classes: ', error);
    }
  };
};

export const getEcap = (authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, authToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/ecap`,
        init
      );
      const myPlan = response.data.data.ecap ? response.data.data.ecap : {};

      if (response.data.data.ecap) {
        const activityResponse = await API.get(
          'api',
          `/tenant/${getState().tenant.id}/ecap/activities`,
          init
        );

        if (activityResponse.data.data.activities) {
          const categoryObj = {
            Extracurricular: [],
            Work: [],
            Volunteer: [],
            Achievement: [],
          };

          activityResponse.data.data.activities.forEach((activity) => {
            Object.keys(categoryObj).forEach((category) => {
              if (activity.category === category) {
                categoryObj[category].push(activity);
              }
            });
          });
          Object.keys(categoryObj).forEach((category) => {
            myPlan[category] = categoryObj[category];
          });
        }

        dispatch({
          type: SET_ECAP,
          payload: { plan: myPlan },
        });
      }
    } catch (error) {
      console.error('Ecap not retrieved: ', error);
    }
  };
};

export const saveEcap = (authToken, plan, firstEntry = false) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          plan,
          firstEntry,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${getState().onboarding.userData.user.tenantId}/ecap`,
        init
      );
      if (!isEmpty(response.data.data.userProgress)) {
        const userData = getState().onboarding.userData;
        batch(() => {
          dispatch({
            type: SET_ECAP,
            payload: { plan },
          });
          dispatch({
            type: GET_USER_DATA,
            payload: {
              userData: {
                ...userData,
                userProgress: response.data.data.userProgress,
              },
            },
          });
        });
      } else {
        dispatch({
          type: SET_ECAP,
          payload: { plan },
        });
      }
    } catch (error) {
      console.error('Ecap not saved: ', error);
    }
  };
};

export const setEcapClassesMatched = (classes_matched) => {
  /* eslint-disable no-unused-vars */
  return (dispatch, getState) => {
    dispatch({
      payload: { classes_matched },
      type: SET_ECAP_CLASSES_MATCHED,
    });
  };
};

export const setEcap = (plan) => {
  return async (dispatch, getState) => {
    try {
      const oldEcap = getState().ecap.plan;

      Object.keys(plan).forEach((key) => {
        if (plan[key]) {
          oldEcap[key] = plan[key];
        }
      });
      dispatch({
        type: SET_ECAP,
        payload: { plan: oldEcap },
      });
    } catch (error) {
      console.error('Ecap not set: ', error);
    }
  };
};

// TODO: Use this for new ecap files after Feb 8
export const setEcap_V2 = (plan) => {
  return async (dispatch, getState) => {
    try {
      const oldEcap = JSON.parse(JSON.stringify(getState().ecap.plan));

      Object.keys(plan).forEach((key) => {
        if (plan[key]) {
          oldEcap[key] = plan[key];
        }
      });
      dispatch({
        type: SET_ECAP,
        payload: { plan: oldEcap },
      });
    } catch (error) {
      console.error('Ecap not set: ', error);
    }
  };
};

export const setActionPlan = (actionPlan) => {
  return async (dispatch, getState) => {
    try {
      const oldActionPlan = getState().ecap.actionPlan;

      Object.keys(actionPlan).forEach((key) => {
        if (actionPlan[key]) {
          oldActionPlan[key] = actionPlan[key];
        }
      });
      dispatch({
        type: SET_ACTION_PLAN,
        payload: { actionPlan: oldActionPlan },
      });
    } catch (error) {
      console.error('Action Plan not set: ', error);
    }
  };
};

export const setCookiesConsent = () => ({
  type: SET_COOKIES_CONSENT,
  payload: {},
});

export const setModalImage = (image) => ({
  type: SET_MODAL_IMAGE,
  payload: image,
});

function formatToObject(newObject) {
  return {
    id: newObject.id || null,
    career_id: newObject.career_id || newObject.id || null,
    user_id: newObject.user_id || null,
    titleEn: newObject.titleEn || null,
    titleEs: newObject.titleEs || null,
    titleFr: newObject.titleFr || null,
    tasksEn: newObject.tasksEn || {},
    tasksEs: newObject.tasksEs || {},
    tasksFr: newObject.tasksFr || null,
    onetCode: newObject.onetCode,
    descriptionEn: newObject.descriptionEn || null,
    descriptionEs: newObject.descriptionEs || null,
    descriptionFr: newObject.descriptionFr || null,
    careerOutlook: newObject.careerOutlook,
    categorytitleEn: newObject.categorytitleEn || null,
    categorytitleEs: newObject.categorytitleEs || null,
    categorytitleFr: newObject.categorytitleFr || null,
    categoryImageUrl1: newObject.categoryImageUrl1 || null,
    categoryImageUrl2: newObject.categoryImageUrl2 || null,
    categoryImageUrl3: newObject.categoryImageUrl3 || null,
    communication: newObject.communication || null,
    criticalThinking: newObject.criticalThinking || null,
    mathematics: newObject.mathematics || null,
    problemSolving: newObject.problemSolving || null,
    computerTechnical: newObject.computerTechnical || null,
    creativity: newObject.creativity || null,
    interpersonal: newObject.interpersonal || null,
  };
}
let listOfFavorites = [];

export const addFavorite = (authToken, userId, careerId, career) => {
  return async (dispatch, getState) => {
    try {
      // Check if the career with the same ID already exists in the favorites
      const isCareerExist = getState().onboarding.favorites.some(
        (favCareer) => favCareer.career_id === careerId
      );

      // Store the list of favorites before attempting to add the career
      listOfFavorites = getState().onboarding.favorites;

      // If the career already exists in favorites, do nothing
      if (isCareerExist) {
        return;
      }

      // Check if career is not empty or undefined
      if (career) {
        // Dispatch action to set career in store
        dispatch({
          type: SET_FAVORITES,
          payload: [...getState().onboarding.favorites, formatToObject(career)], // Include the new career object in the payload
        });
      }

      const init = getAmplifyConfig(
        {
          careerId,
        },
        authToken
      );

      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/addFavorite`,
        init
      );

      if (!isEmpty(response.data.data.progress)) {
        const userData = getState().onboarding.userData;
        batch(() => {
          dispatch({
            type: SET_FAVORITES,
            payload: response.data.data.favorites,
          });
          dispatch({
            type: GET_USER_DATA,
            payload: {
              userData: {
                ...userData,
                userProgress: response.data.data.progress,
              },
            },
          });
        });
        return response;
      }

      dispatch({
        type: SET_FAVORITES,
        payload: response.data.data.favorites,
      });

      return response;
    } catch (error) {
      // Revert the favorites store back to the previous state
      dispatch({
        type: SET_FAVORITES,
        payload: listOfFavorites,
      });
      console.error('Favorite not added: ', error);
    }
  };
};

export const removeFavorite = (userId, authToken, careerId) => {
  return async (dispatch, getState) => {
    try {
      // Check if the careerId exists in the favorites store
      const isCareerExist = getState().onboarding.favorites.some(
        (favCareer) => favCareer.career_id === careerId
      );

      // Store the list of favorites before attempting to remove the career
      listOfFavorites = getState().onboarding.favorites;

      // If the careerId exists, remove it from the favorites
      if (isCareerExist) {
        const updatedFavorites = getState().onboarding.favorites.filter(
          (favCareer) => favCareer.career_id !== careerId
        );
        dispatch({
          type: SET_FAVORITES,
          payload: updatedFavorites,
        });
      }

      const init = getAmplifyConfig(
        {
          careerId,
        },
        authToken
      );

      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/removeFavorite`,
        init
      );

      if (!isEmpty(response.data.data.progress)) {
        const userData = getState().onboarding.userData;
        batch(() => {
          dispatch({
            type: SET_FAVORITES,
            payload: response.data.data.favorites,
          });
          dispatch({
            type: GET_USER_DATA,
            payload: {
              userData: {
                ...userData,
                userProgress: response.data.data.progress,
              },
            },
          });
        });
        return response;
      }

      dispatch({
        type: SET_FAVORITES,
        payload: response.data.data.favorites,
      });

      return response;
    } catch (error) {
      // Revert the favorites store back to the previous state
      dispatch({
        type: SET_FAVORITES,
        payload: listOfFavorites,
      });

      console.error('Favorite not removed: ', error);
    }
  };
};

export const resetUserFavorites = (userId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/resetUserFavorites`,
        init
      );
      if (!isEmpty(response.data.data.progress)) {
        const userData = getState().onboarding.userData;
        batch(() => {
          dispatch({
            type: SET_FAVORITES,
            payload: response.data.data.favorites,
          });
          dispatch({
            type: GET_USER_DATA,
            payload: {
              userData: {
                ...userData,
                userProgress: response.data.data.progress,
              },
            },
          });
        });
        return response;
      }
      dispatch({
        type: SET_FAVORITES,
        payload: response.data.data.favorites,
      });

      return response;
    } catch (error) {
      console.error('Favorites not reset: ', error);
    }
  };
};

export const getFavoritesByUser = (userId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/favorites`,
        init
      );

      dispatch({
        type: SET_FAVORITES,
        payload: response.data.data,
      });
      return response;
    } catch (error) {
      console.error('Favorites for user not retrieved: ', error);
    }
  };
};

export const updateUserProfile = (user, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ user }, authToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/updateUserProfile`,
        init
      );
      dispatch({
        type: GET_USER_DATA,
        payload: { userData: response.data.data },
      });
      return response.status;
      // dispatch(setLoading(false));
    } catch (error) {
      console.error('Failed to update user information: ', error);
    }
  };
};

export const getUserDashboardInfo = (userId, tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          riasecValues: userId.riasecValues,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/userId/${userId}/getAdvisorUserProfile`,
        init
      );

      const degreeChoices = {
        1: '',
        2: '',
        3: '',
      };

      /* eslint-disable-next-line */
      for (const x of response.data.data.careerGameResults) {
        if (x.degreeId !== null && x.chosenOrder !== null) {
          /* eslint-disable-next-line */
          const degreeResponse = await API.get(
            'api',
            `/tenant/${tenantId}/degree/${x.degreeId}`,
            init
          );

          const degreeMatch = degreeResponse.data.data;

          // The chosenOrder can also tell you which tab it was selected for
          if (degreeMatch === undefined) {
            degreeChoices[x.chosenOrder] = '';
          } else {
            degreeChoices[x.chosenOrder] = degreeMatch;
          }

          // Add in the degree match to the degreeCareerIdMap if it isn't there
          // (but a match exists) so users don't get stuck in the path pairing page
          if (!response.data.data.degreeCareerIdMap[x.id]) {
            response.data.data.degreeCareerIdMap[x.id] = [degreeMatch];
          }
        }
      }

      // adding career image to degree
      /* eslint-disable-next-line */
      for (const id in response.data.data.degreeCareerIdMap) {
        const careerMatchingDegree = response.data.data.careerGameResults.find(
          (career) => career.id === Number(id)
        );

        for (
          let i = 0;
          i < response.data.data.degreeCareerIdMap[id].length;
          i++
        ) {
          response.data.data.degreeCareerIdMap[id][i].career_image =
            careerMatchingDegree.image;
        }
      }

      const userProfileObj = response.data.data;
      userProfileObj.degreeChoices = degreeChoices;

      dispatch({
        type: GET_ADVISOR_USER_PROFILE,
        payload: userProfileObj,
      });
      return userProfileObj;
    } catch (error) {
      console.error('Could not get user dashboard info: ', error);
    }
  };
};

export const updateAdvisorDashboardUserProfile = (user, authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ user }, authToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/updateAdvisorDashboardUserProfile`,
        init
      );
      await dispatch(getUserDashboardInfo(user.id, user.tenantId));
      return response.status;
    } catch (error) {
      console.error('Failed to update user information: ', error);
    }
  };
};

export const updateUserProgress = (authToken, updateObj) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(updateObj, authToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/updateUserProgress`,
        init
      );
      dispatch({
        type: GET_USER_DATA,
        payload: { userData: response.data.data },
      });
    } catch (error) {
      console.error('Could not update user progress ', error);
    }
  };
};

export const getSpecificTenantLogo = () => {
  return async (dispatch, getState) => {
    try {
      const tenantId = getState().onboarding.userData.user.tenantId;
      const init = getAmplifyConfig({});
      const response = await API.get('api', `/tenant/${tenantId}/logo`, init);

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve tenant logo', error);
    }
  };
};

export const getRoleInfo = (userId, userRole, tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { userId, userRole },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getRoleInfo`,
        init
      );
      return response.data.data.userInfo;
    } catch (error) {
      console.error('Could not get role info for user', error);
    }
  };
};

export const editRole = (tenantId, authToken, editInfo) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ editInfo }, authToken);
      const initialResponse = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/editRole`,
        init
      );
      if (initialResponse.status === 200) {
        const config = getAmplifyConfig(
          {
            checkedFilters: {},
            searchString: '',
            currentPage: 1,
            limit: false,
          },
          getState().auth.jwtToken
        );
        const response = await API.post(
          'api',
          `/tenant/${tenantId}/users`,
          config
        );
        if (response.data === null) {
          dispatch({ type: GET_ALL_USERS, payload: null });
        } else {
          dispatch({ type: GET_ALL_USERS, payload: response.data.data });
        }
        return response.status;
      }
    } catch (error) {
      console.error('Role not edited: ', error);
    }
  };
};

export const getPopulationBySchool = (offset = 0) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({ offset }, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/getPopulationBySchool`,
        init
      );
      return response.data.data;
    } catch (error) {
      console.error('Could not get population by school: ', error);
    }
  };
};

export const getPopulationBySchoolCsv = () => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/getPopulationBySchoolCsv`,
        init
      );
      return response.data.data;
    } catch (error) {
      console.error(
        'Could not get CSV data for the population by school counts:',
        error
      );
    }
  };
};

export const getRolePermissions = (authToken) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, authToken);
      const response = await API.get(
        'api',
        `/tenant/${getState().tenant.id}/getRolePermissions`,
        init
      );
      const permissions = response.data.data;
      const existingChecked = {};
      const existingFilters = cloneDeep(
        getState().admin.permissions.usersFilters
      );
      Object.keys(existingFilters).forEach((key) => {
        const filtered = existingFilters[key].filter((x) => x.checked === true);
        if (filtered.length > 0) {
          existingChecked[key] = filtered;
        }
      });
      Object.keys(permissions.usersFilters).map((key) => {
        return permissions.usersFilters[key].forEach((val) => {
          if (existingChecked[key]) {
            if (existingChecked[key].find((x) => x.titleEn === val.titleEn)) {
              /* eslint-disable */
              val.checked = true;
              /* eslint-enable */
            }
          }
        });
      });
      Object.keys(permissions.usersFilters).map((key) => {
        permissions.usersFilters[key] = Object.values(
          permissions.usersFilters[key]
        ).sort((a, b) => {
          if (a.checked === b.checked) {
            return 1;
          }
          if (a.checked && !b.checked) {
            return -1;
          }
          return 1;
        });
      });
      dispatch({ type: SET_ROLE_PERMISSIONS, payload: permissions });
      return permissions;
    } catch (error) {
      console.error('Could not get role permissions: ', error);
    }
  };
};

export const setUserIPAddress = (userId, authToken, ipAddress) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          ipAddress,
        },
        authToken
      );
      const response = await API.post(
        'api',
        `/tenant/${getState().tenant.id}/user/${userId}/setUserIPAddress`,
        init
      );
      return response;
    } catch (error) {
      console.error('User IP Address not saved: ', error);
    }
  };
};

export const getEligibleUnassignedAdvisorsForUser = (
  tenantId,
  userId,
  highSchool
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { userId, highSchool },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getEligibleUnassignedAdvisorsForUser`,
        init
      );
      let result = response.data.data;
      if (result === undefined) result = [];
      return result;
    } catch (error) {
      console.error('Could not get advisors for user: ', error);
    }
  };
};

export const assignAdvisorsToUser = (userId, tenantId, advisorObjs) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { userId, advisorObjs },
        getState().auth.jwtToken
      );
      await API.post('api', `/tenant/${tenantId}/assignAdvisors`, init);
    } catch (error) {
      console.error('Could not assign advisor(s): ', error);
    }
  };
};

export const unassignAdvisorFromUser = (userId, tenantId, advisorId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        { userId, advisorId },
        getState().auth.jwtToken
      );
      await API.post('api', `/tenant/${tenantId}/unassignAdvisor`, init);
    } catch (error) {
      console.error('Could not unassign advisor(s): ', error);
    }
  };
};

export const getHighestLevelOfEducationCounts = (tenantId, filters) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          filters,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getHighestLevelOfEducationCounts`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error(
        'Could not retrieve the highest level of education counts: ',
        error
      );
    }
  };
};

export const getHighestLevelOfEducationCountsCsv = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getHighestLevelOfEducationCountsCsv`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error(
        'Could not get CSV data for the highest level of education counts: ',
        error
      );
    }
  };
};

export const getTopChosenCareers = (tenantId, filters) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          filters,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getTopChosenCareers`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve the top chosen careers: ', error);
    }
  };
};

export const getTopChosenCareersCsv = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getTopChosenCareersCsv`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error(
        'Could not get CSV data for the top chosen careers: ',
        error
      );
    }
  };
};

export const getTopChosenAcademicOfferings = (tenantId, filters) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          filters,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getTopChosenAcademicOfferings`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error(
        'Could not retrieve the top chosen academic offerings: ',
        error
      );
    }
  };
};

export const getTopChosenAcademicOfferingsCsv = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getTopChosenAcademicOfferingsCsv`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error(
        'Could not get CSV data for the top chosen academic offerings: ',
        error
      );
    }
  };
};

export const getEcapCompletionsByClass = (tenantId, offset) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          offset,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getEcapCompletionsByClass`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve ECAP completions by class: ', error);
    }
  };
};

export const getTotalEcapCompletions = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getTotalEcapCompletions`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve total ECAP completions: ', error);
    }
  };
};

export const getEcapCompletionsCsv = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getEcapCompletionsCsv`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not get CSV data for ECAP completions: ', error);
    }
  };
};

export const getListOfTenantNames = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getListOfTenantNames`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not get list of tenant names: ', error);
    }
  };
};

export const getNewUserReport = (
  tenantId,
  reportTenantName,
  startDate,
  endDate,
  page
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          tenantName: reportTenantName,
          startDate,
          endDate,
          page,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getNewUserReport`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve new user report: ', error);
    }
  };
};

export const insertTenantTableData = (body) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(body, getState().auth.jwtToken);
      const response = await API.post('api', `/tenant`, init);
      return response.data.data.tenant.id;
    } catch (error) {
      console.error('Could not insert tenant table data: ', error);
    }
  };
};

export const updateTenantPortraitImage = (body) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(body, getState().auth.jwtToken);
      const response = await API.post('api', `/tenant/portrait`, init);
      console.log('response: ', response);
      return response.data.data.tenant.id;
    } catch (error) {
      console.error('Could not update tenant portrait data: ', error);
    }
  };
};

export const getNewUserReportPages = (
  tenantId,
  reportTenantName,
  startDate,
  endDate
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          tenantName: reportTenantName,
          startDate,
          endDate,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getNewUserReportPages`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve new user report page count: ', error);
    }
  };
};

export const getNewUserReportCsv = (
  tenantId,
  reportTenantName,
  startDate,
  endDate,
  page
) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(
        {
          tenantName: reportTenantName,
          startDate,
          endDate,
          page,
        },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/getNewUserReportCsv`,
        init
      );

      return response.data.data;
    } catch (error) {
      console.error('Could not retrieve new user report CSV: ', error);
    }
  };
};

export const getAllTenants = () => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get('api', `/getAllTenants`, init);

      return response.data.data.tenants;
    } catch (error) {
      console.error('Could not retrieve all tenants for Super User: ', error);
    }
  };
};

export const getCareerBasicsForTenant = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getCareerBasicsForTenant`,
        init
      );

      return response.data.data.tenantCareers;
    } catch (error) {
      console.error('Could not retrieve all career basics for tenant: ', error);
    }
  };
};

export const getCareerDetails = (careerId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/getCareerDetails/${careerId}`,
        init
      );

      return response.data.data.careerDetails;
    } catch (error) {
      console.error('Could not retrieve career details: ', error);
    }
  };
};

export const getTenantDetails = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/getTenantDetails/${tenantId}`,
        init
      );
      return response.data.data.tenantDetails;
    } catch (error) {
      console.error('Could not retrieve tenant details: ', error);
    }
  };
};

export const getDegreeBasicsForTenant = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/tenant/${tenantId}/getDegreeBasicsForTenant`,
        init
      );
      return response.data.data.tenantDegrees;
    } catch (error) {
      console.error('Could not retrieve all degree basics for tenant: ', error);
    }
  };
};

// TODO: pre-check all the data
// pass array of .text()-ed data to this action
// send an array of data to the back end
// iterate through the data and automatically check them against the csv schemas
// do this with an object? on both ends? or something?
// if we get an error, return that error and display it

export const updatePublishedAndGetTenants = (tenantId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      await API.post(
        'api',
        `/tenant/${tenantId}/updateTenantPublishedValue`,
        init
      );
      const response = await API.get('api', `/getAllTenants`, init);
      return response.data.data.tenants;
    } catch (e) {
      console.log('Could not update published value for tenant: ', e);
    }
  };
};

export const insertCareerCategories = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const careerCategoriesData = await data.text();
      const init = getAmplifyConfig(
        { csv: careerCategoriesData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/careerCategoryImport`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const updateTenantDegrees = (
  tenantId,
  jobIdentifier,
  degrees,
  degreeTranslations
) => {
  return async (dispatch, getState) => {
    try {
      const degreesData = degrees ? await degrees.text() : null;
      const degreeTranslationsData = degreeTranslations
        ? await degreeTranslations.text()
        : null;
      const init = getAmplifyConfig(
        {
          tenantId,
          degrees: degreesData,
          jobIdentifier,
          degreeTranslations: degreeTranslationsData,
        },
        getState().auth.jwtToken
      );
      const response = await API.post('api', `/updateTenantDegrees`, init);
      return response.data;
    } catch (e) {
      console.log('error:', e);
      if (e.response.statusText === 'Service Unavailable')
        e.response.data =
          'Something went wrong with PeopleSoft. Please try again';
      return e.response;
    }
  };
};

export const updateTenantCareers = (
  tenantId,
  careers,
  careerCategories,
  relatedCareers,
  jobIdentifier,
  degrees,
  degreeTranslations
) => {
  return async (dispatch, getState) => {
    try {
      const careersData = await careers.text();
      const careerCategoriesData = await careerCategories.text();
      const relatedCareersData = relatedCareers
        ? await relatedCareers.text()
        : null;
      const degreesData = degrees ? await degrees.text() : null;
      const degreeTranslationsData = degreeTranslations
        ? await degreeTranslations.text()
        : null;
      const init = getAmplifyConfig(
        {
          tenantId,
          careers: careersData,
          careerCategories: careerCategoriesData,
          relatedCareers: relatedCareersData,
          degrees: degreesData,
          degreeTranslations: degreeTranslationsData,
          jobIdentifier,
        },
        getState().auth.jwtToken
      );
      const response = await API.post('api', `/updateTenantCareers`, init);
      return response.data;
    } catch (e) {
      console.log('error: ', e);
      if (e?.response?.statusText === 'Service Unavailable')
        e.response.data =
          'Something went wrong with PeopleSoft. Please try again';
      return e.response;
    }
  };
};

export const insertCareers = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const careersData = await data.text();
      const init = getAmplifyConfig(
        { csv: careersData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/careerImport`,
        init
      );
      return response;
    } catch (e) {
      return e.response;
    }
  };
};

export const checkJobStatus = (identifier) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/${identifier}/checkJobStatus`,
        init
      );
      return response.data.data;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertDegrees = (tenantId, data, identifier = null) => {
  return async (dispatch, getState) => {
    try {
      const degreesData = await data.text();
      const init = getAmplifyConfig(
        { degrees: degreesData, identifier },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/degree/insertDegreesWithIdentifier`,
        init
      );
      return response;
    } catch (e) {
      console.log('e: ', e);
      return e.response;
    }
  };
};

export const insertHighSchools = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const schoolsData = await data.text();
      const init = getAmplifyConfig(
        { csv: schoolsData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertSchools`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertGameImages = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const gameImagesData = await data.text();
      const init = getAmplifyConfig(
        { csv: gameImagesData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/gameImageImport`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertHighSchoolClasses = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const highSchoolClassesData = await data.text();
      const init = getAmplifyConfig(
        { csv: highSchoolClassesData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertHSClasses`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertSubjectDifficultyMappings = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const subjectDifficultyMapping = await data.text();
      const init = getAmplifyConfig(
        { csv: subjectDifficultyMapping },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertSubjectDifficultyMapping`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertDegreeSubjectDifficultyMappings = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const degreeSubjectDifficultyMapping = await data.text();
      const init = getAmplifyConfig(
        { csv: degreeSubjectDifficultyMapping },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertDegreeSubjectDifficultyMapping`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertSkillsCourseRecommendations = (tenantId, data) => {
  return async (dispatch, getState) => {
    try {
      const skillsCourseRecommendationsData = await data.text();
      const init = getAmplifyConfig(
        { csv: skillsCourseRecommendationsData },
        getState().auth.jwtToken
      );
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/CPECourseMappingImport`,
        init
      );
      return response;
    } catch (e) {
      console.log(e);
      return e.response;
    }
  };
};

export const insertFeaturesByName = (tenantId, features) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig(features, getState().auth.jwtToken);
      const response = await API.post(
        'api',
        `/tenant/${tenantId}/insertFeaturesByName`,
        init
      );
      return response.data.data.ids;
    } catch (error) {
      console.error('Could not insert features by name: ', error);
    }
  };
};

export const getDegreeDetails = (degreeId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/getDegreeDetails/${degreeId}`,
        init
      );
      return response.data.data.degreeDetails;
    } catch (error) {
      console.error('Could not retrieve degree details: ', error);
    }
  };
};

export const getEarningCapacity = (careerId) => {
  return async (dispatch, getState) => {
    try {
      const init = getAmplifyConfig({}, getState().auth.jwtToken);
      const response = await API.get(
        'api',
        `/getEarningCapacity/${careerId}`,
        init
      );
      return response.data.data.earning_capacity;
    } catch (error) {
      console.error('Could not retrieve earning capacity for career: ', error);
    }
  };
};

export const setCurrentLocale = (locale) => {
  i18n.changeLanguage(locale);

  return (dispatch) => {
    dispatch({
      type: SET_CURRENT_LOCALE,
      payload: locale,
    });
  };
};
